import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AddSpotTradingCoinPage.css";

const ALLOWED_CHAINS = ["BEP20", "ERC20", "TRC20", "POLYGON", "SOLANA"];
const FALLBACK_REGEX = {
  BEP20: "^(0x)?[0-9a-fA-F]{40}$",
  ERC20: "^(0x)?[0-9a-fA-F]{40}$",
  POLYGON: "^(0x)?[0-9a-fA-F]{40}$",
  TRC20: "^(T|t)[A-Za-z1-9]{33}$",
  SOLANA: "^[1-9A-HJ-NP-Za-km-z]{32,44}$",
};
const CATEGORIES = ["GLOBAL", "LOCAL"];
const STATUS_VALUES = ["ACTIVE", "INACTIVE"];
const IMAGE_ACCEPT = "image/jpeg,image/jpg,image/png";
const MAX_IMAGE_SIZE_MB = 2;

const validateContractAddress = (value, chain, supportedChains) => {
  if (!value?.trim()) return "Contract address is required";
  const ch = supportedChains?.find((c) => c.id === chain);
  const regexStr = ch?.contractAddressRegex || FALLBACK_REGEX[chain];
  if (!regexStr) return null;
  try {
    const regex = new RegExp(regexStr);
    return regex.test(value.trim()) ? null : `Invalid format for ${chain}`;
  } catch {
    return "Invalid format";
  }
};

const validateNumber = (val, field, min = 0) => {
  const n = Number(val);
  if (val === "" || val == null) return `${field} is required`;
  if (Number.isNaN(n)) return `${field} must be a number`;
  if (n < min) return `${field} must be >= ${min}`;
  return null;
};

const AddSpotTradingCoinPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successModalData, setSuccessModalData] = useState(null);

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [category, setCategory] = useState("GLOBAL");
  const [chains, setChains] = useState([]);
  const [coinImage, setCoinImage] = useState(null);
  const [description, setDescription] = useState("");
  const [chainData, setChainData] = useState({});

  const supportedChains = config?.data?.supportedChains ?? [];

  const resetForm = useCallback(() => {
    setName("");
    setShortName("");
    setCategory("GLOBAL");
    setChains([]);
    setCoinImage(null);
    setDescription("");
    setChainData({});
    setFieldErrors({});
    setSubmitError("");
  }, []);

  const handleSuccessModalClose = () => {
    setSuccessModalData(null);
    resetForm();
  };

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError("");
    const res = await AdminService.getCoinsConfig();
    setConfigLoading(false);
    if (res?.success) {
      setConfig(res);
    } else {
      setConfigError(res?.message || "Failed to load coins config.");
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChainToggle = (chainId) => {
    const next = chains.includes(chainId) ? chains.filter((c) => c !== chainId) : [...chains, chainId].sort();
    setChains(next);
    if (!next.includes(chainId)) {
      setChainData((d) => {
        const copy = { ...d };
        delete copy[chainId];
        return copy;
      });
    } else {
      setChainData((d) => ({
        ...d,
        [chainId]: {
          contract_address: "",
          decimals: "",
          min_withdrawal: "",
          max_withdrawal: "",
          min_deposit: "",
          max_deposit: "",
          withdrawal_fee: "",
          deposit_status: "ACTIVE",
          withdrawal_status: "ACTIVE",
          ...(d[chainId] || {}),
        },
      }));
    }
    setFieldErrors((e) => {
      const copy = { ...e };
      Object.keys(copy).forEach((k) => {
        if (k.startsWith(`chain_`) && !next.some((c) => k.includes(c))) delete copy[k];
      });
      return copy;
    });
  };

  const updateChainField = (chainId, field, value) => {
    setChainData((d) => ({
      ...d,
      [chainId]: { ...(d[chainId] || {}), [field]: value },
    }));
    setFieldErrors((e) => {
      const key = `chain_${chainId}_${field}`;
      const copy = { ...e };
      delete copy[key];
      return copy;
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!name?.trim()) errors.name = "Coin name is required";
    else if (name.trim().length < 2) errors.name = "Coin name must be at least 2 characters";

    if (!shortName?.trim()) errors.short_name = "Symbol is required";
    else if (shortName.trim().length < 2 || shortName.trim().length > 10) {
      errors.short_name = "Symbol must be 2–10 characters";
    } else if (!/^[A-Z0-9]+$/i.test(shortName.trim())) {
      errors.short_name = "Symbol can only contain letters and numbers";
    }

    if (!CATEGORIES.includes(category)) errors.category = "Invalid category";

    if (!chains.length) errors.chains = "Select at least one chain";

    if (!coinImage) errors.coin_image = "Coin icon image is required";
    else if (coinImage.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      errors.coin_image = `Image must be under ${MAX_IMAGE_SIZE_MB} MB`;
    } else if (!IMAGE_ACCEPT.split(",").some((t) => coinImage.type === t)) {
      errors.coin_image = "Image must be JPG, JPEG or PNG";
    }

    chains.forEach((ch) => {
      const cd = chainData[ch] || {};
      const addrErr = validateContractAddress(cd.contract_address, ch, supportedChains);
      if (addrErr) errors[`chain_${ch}_contract_address`] = addrErr;

      const decErr = validateNumber(cd.decimals, "Decimals", 0);
      if (decErr) errors[`chain_${ch}_decimals`] = decErr;

      const minWErr = validateNumber(cd.min_withdrawal, "Min withdrawal", 0);
      if (minWErr) errors[`chain_${ch}_min_withdrawal`] = minWErr;

      const maxWErr = validateNumber(cd.max_withdrawal, "Max withdrawal", 0);
      if (maxWErr) errors[`chain_${ch}_max_withdrawal`] = maxWErr;
      else if (Number(cd.min_withdrawal) > Number(cd.max_withdrawal)) {
        errors[`chain_${ch}_max_withdrawal`] = "Max must be >= min";
      }

      const minDErr = validateNumber(cd.min_deposit, "Min deposit", 0);
      if (minDErr) errors[`chain_${ch}_min_deposit`] = minDErr;

      const maxDErr = validateNumber(cd.max_deposit, "Max deposit", 0);
      if (maxDErr) errors[`chain_${ch}_max_deposit`] = maxDErr;
      else if (Number(cd.min_deposit) > Number(cd.max_deposit)) {
        errors[`chain_${ch}_max_deposit`] = "Max must be >= min";
      }

      const feeErr = validateNumber(cd.withdrawal_fee, "Withdrawal fee", 0);
      if (feeErr) errors[`chain_${ch}_withdrawal_fee`] = feeErr;

      if (!STATUS_VALUES.includes(cd.deposit_status)) {
        errors[`chain_${ch}_deposit_status`] = "Invalid status";
      }
      if (!STATUS_VALUES.includes(cd.withdrawal_status)) {
        errors[`chain_${ch}_withdrawal_status`] = "Invalid status";
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("coin-image", coinImage);
    formData.append("name", name.trim());
    formData.append("short_name", shortName.trim().toUpperCase());
    formData.append("chain", JSON.stringify(chains));
    formData.append("category", category);

    const contract_address = {};
    const decimals = {};
    const min_withdrawal = {};
    const max_withdrawal = {};
    const min_deposit = {};
    const max_deposit = {};
    const withdrawal_fee = {};
    const deposit_status = {};
    const withdrawal_status = {};

    chains.forEach((ch) => {
      const cd = chainData[ch] || {};
      contract_address[ch] = (cd.contract_address || "").trim();
      decimals[ch] = Number(cd.decimals) || 0;
      min_withdrawal[ch] = Number(cd.min_withdrawal) || 0;
      max_withdrawal[ch] = Number(cd.max_withdrawal) || 0;
      min_deposit[ch] = Number(cd.min_deposit) || 0;
      max_deposit[ch] = Number(cd.max_deposit) || 0;
      withdrawal_fee[ch] = Number(cd.withdrawal_fee) || 0;
      deposit_status[ch] = cd.deposit_status || "ACTIVE";
      withdrawal_status[ch] = cd.withdrawal_status || "ACTIVE";
    });

    formData.append("contract_address", JSON.stringify(contract_address));
    formData.append("decimals", JSON.stringify(decimals));
    formData.append("min_withdrawal", JSON.stringify(min_withdrawal));
    formData.append("max_withdrawal", JSON.stringify(max_withdrawal));
    formData.append("min_deposit", JSON.stringify(min_deposit));
    formData.append("max_deposit", JSON.stringify(max_deposit));
    formData.append("withdrawal_fee", JSON.stringify(withdrawal_fee));
    formData.append("deposit_status", JSON.stringify(deposit_status));
    formData.append("withdrawal_status", JSON.stringify(withdrawal_status));

    if (description?.trim()) formData.append("description", description.trim());

    setSubmitLoading(true);
    const res = await AdminService.createCoin(formData);
    setSubmitLoading(false);

    if (res?.success) {
      setSuccessModalData({
        name: name.trim(),
        shortName: shortName.trim().toUpperCase(),
        category,
        chains: [...chains],
      });
    } else {
      setSubmitError(res?.message || "Failed to create coin.");
    }
  };

  if (configLoading) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Add Spot Trading Coin</span>
            </p>
            <h1 className="dashboard-hero__title">Add Spot Trading Coin</h1>
          </div>
        </section>
        <div className="addcoin-loading">
          <i className="fas fa-spinner fa-spin" /> Loading configuration…
        </div>
      </main>
    );
  }

  if (configError) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Add Spot Trading Coin</span>
            </p>
            <h1 className="dashboard-hero__title">Add Spot Trading Coin</h1>
          </div>
        </section>
        <div className="addcoin-error">
          <i className="fas fa-exclamation-triangle" />
          <p>{configError}</p>
          <button type="button" className="btn btn-primary" onClick={fetchConfig}>
            <i className="fas fa-redo" /> Retry
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/coinListDetails">Coin List Requests</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Add Spot Trading Coin</span>
          </p>
          <h1 className="dashboard-hero__title">Add Spot Trading Coin</h1>
          <p className="dashboard-hero__subtitle">
            Add a new coin for spot trading with chain-specific configuration
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-plus-circle" />
        </div>
      </section>

      <div className="dashboard-body">
        <form onSubmit={handleSubmit} className="addcoin-form">
          {submitError && (
            <div className="addcoin-alert addcoin-alert--error" role="alert">
              <i className="fas fa-exclamation-circle" />
              <span>{submitError}</span>
            </div>
          )}

          <section className="addcoin-card">
            <h2 className="addcoin-card__title"><i className="fas fa-info-circle" /> Basic Information</h2>
            <div className="addcoin-grid">
              <div className="addcoin-field">
                <label htmlFor="name">Coin Name <span className="addcoin-required">*</span></label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setFieldErrors((e2) => { const n = { ...e2 }; delete n.name; return n; }); }}
                  placeholder="e.g. Tether"
                  className={fieldErrors.name ? "addcoin-input--error" : ""}
                  maxLength={50}
                />
                {fieldErrors.name && <span className="addcoin-error-msg">{fieldErrors.name}</span>}
              </div>
              <div className="addcoin-field">
                <label htmlFor="short_name">Symbol (Short Name) <span className="addcoin-required">*</span></label>
                <input
                  id="short_name"
                  type="text"
                  value={shortName}
                  onChange={(e) => { setShortName(e.target.value.toUpperCase()); setFieldErrors((e2) => { const n = { ...e2 }; delete n.short_name; return n; }); }}
                  placeholder="e.g. USDT"
                  className={fieldErrors.short_name ? "addcoin-input--error" : ""}
                  maxLength={10}
                  style={{ textTransform: "uppercase" }}
                />
                {fieldErrors.short_name && <span className="addcoin-error-msg">{fieldErrors.short_name}</span>}
              </div>
              <div className="addcoin-field">
                <label htmlFor="category">Category <span className="addcoin-required">*</span></label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={fieldErrors.category ? "addcoin-input--error" : ""}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {fieldErrors.category && <span className="addcoin-error-msg">{fieldErrors.category}</span>}
              </div>
              <div className="addcoin-field">
                <label>Coin Icon Image <span className="addcoin-required">*</span></label>
                <input
                  type="file"
                  accept={IMAGE_ACCEPT}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setCoinImage(file || null);
                    setFieldErrors((e2) => { const n = { ...e2 }; delete n.coin_image; return n; });
                  }}
                  className={fieldErrors.coin_image ? "addcoin-input--error" : ""}
                />
                <span className="addcoin-hint">JPG, JPEG or PNG. Max {MAX_IMAGE_SIZE_MB} MB</span>
                {fieldErrors.coin_image && <span className="addcoin-error-msg">{fieldErrors.coin_image}</span>}
                {coinImage && <span className="addcoin-file-preview"><i className="fas fa-check" /> {coinImage.name}</span>}
              </div>
              <div className="addcoin-field addcoin-field--full">
                <label htmlFor="description">Description (optional)</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the coin"
                  rows={3}
                  className="addcoin-textarea"
                />
              </div>
            </div>
          </section>

          <section className="addcoin-card">
            <h2 className="addcoin-card__title"><i className="fas fa-link" /> Supported Chains <span className="addcoin-required">*</span></h2>
            {fieldErrors.chains && <span className="addcoin-error-msg">{fieldErrors.chains}</span>}
            <div className="addcoin-chains">
              {(supportedChains.length ? supportedChains : ALLOWED_CHAINS.map((id) => ({ id, name: id, network: id }))).map((ch) => (
                <label key={ch.id} className={`addcoin-chain-chip ${chains.includes(ch.id) ? "addcoin-chain-chip--active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={chains.includes(ch.id)}
                    onChange={() => handleChainToggle(ch.id)}
                  />
                  <span>{ch.name}</span>
                  {ch.network && <span className="addcoin-chain-network">{ch.network}</span>}
                </label>
              ))}
            </div>
          </section>

          {chains.length > 0 && (
            <section className="addcoin-card">
              <h2 className="addcoin-card__title"><i className="fas fa-cog" /> Per-Chain Configuration</h2>
              <p className="addcoin-card__desc">Configure contract address, decimals, limits and status for each selected chain.</p>
              {chains.map((chainId) => {
                const ch = supportedChains.find((c) => c.id === chainId) || { id: chainId, name: chainId };
                const cd = chainData[chainId] || {};
                return (
                  <div key={chainId} className="addcoin-chain-block">
                    <h3 className="addcoin-chain-block__title">{ch.name}</h3>
                    <div className="addcoin-grid">
                      <div className="addcoin-field addcoin-field--full">
                        <label>Contract Address <span className="addcoin-required">*</span></label>
                        <input
                          type="text"
                          value={cd.contract_address || ""}
                          onChange={(e) => updateChainField(chainId, "contract_address", e.target.value)}
                          placeholder={ch.contractAddressRegex ? `Format: ${ch.contractAddressRegex}` : "0x..."}
                          className={fieldErrors[`chain_${chainId}_contract_address`] ? "addcoin-input--error" : ""}
                          spellCheck={false}
                        />
                        {fieldErrors[`chain_${chainId}_contract_address`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_contract_address`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Decimals <span className="addcoin-required">*</span></label>
                        <input
                          type="number"
                          min={0}
                          max={18}
                          value={cd.decimals}
                          onChange={(e) => updateChainField(chainId, "decimals", e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={fieldErrors[`chain_${chainId}_decimals`] ? "addcoin-input--error" : ""}
                        />
                        {fieldErrors[`chain_${chainId}_decimals`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_decimals`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Min Withdrawal <span className="addcoin-required">*</span></label>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={cd.min_withdrawal}
                          onChange={(e) => updateChainField(chainId, "min_withdrawal", e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={fieldErrors[`chain_${chainId}_min_withdrawal`] ? "addcoin-input--error" : ""}
                        />
                        {fieldErrors[`chain_${chainId}_min_withdrawal`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_min_withdrawal`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Max Withdrawal <span className="addcoin-required">*</span></label>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={cd.max_withdrawal}
                          onChange={(e) => updateChainField(chainId, "max_withdrawal", e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={fieldErrors[`chain_${chainId}_max_withdrawal`] ? "addcoin-input--error" : ""}
                        />
                        {fieldErrors[`chain_${chainId}_max_withdrawal`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_max_withdrawal`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Min Deposit <span className="addcoin-required">*</span></label>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={cd.min_deposit}
                          onChange={(e) => updateChainField(chainId, "min_deposit", e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={fieldErrors[`chain_${chainId}_min_deposit`] ? "addcoin-input--error" : ""}
                        />
                        {fieldErrors[`chain_${chainId}_min_deposit`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_min_deposit`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Max Deposit <span className="addcoin-required">*</span></label>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={cd.max_deposit}
                          onChange={(e) => updateChainField(chainId, "max_deposit", e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={fieldErrors[`chain_${chainId}_max_deposit`] ? "addcoin-input--error" : ""}
                        />
                        {fieldErrors[`chain_${chainId}_max_deposit`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_max_deposit`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Withdrawal Fee <span className="addcoin-required">*</span></label>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={cd.withdrawal_fee}
                          onChange={(e) => updateChainField(chainId, "withdrawal_fee", e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={fieldErrors[`chain_${chainId}_withdrawal_fee`] ? "addcoin-input--error" : ""}
                        />
                        {fieldErrors[`chain_${chainId}_withdrawal_fee`] && (
                          <span className="addcoin-error-msg">{fieldErrors[`chain_${chainId}_withdrawal_fee`]}</span>
                        )}
                      </div>
                      <div className="addcoin-field">
                        <label>Deposit Status</label>
                        <select
                          value={cd.deposit_status || "ACTIVE"}
                          onChange={(e) => updateChainField(chainId, "deposit_status", e.target.value)}
                        >
                          {STATUS_VALUES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="addcoin-field">
                        <label>Withdrawal Status</label>
                        <select
                          value={cd.withdrawal_status || "ACTIVE"}
                          onChange={(e) => updateChainField(chainId, "withdrawal_status", e.target.value)}
                        >
                          {STATUS_VALUES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          <div className="addcoin-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate("/dashboard/coinListDetails")}
              disabled={submitLoading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitLoading}>
              {submitLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-plus" />}
              {submitLoading ? " Creating…" : " Create Coin"}
            </button>
          </div>
        </form>

        {successModalData && (
          <div className="addcoin-success-overlay" onClick={handleSuccessModalClose}>
            <div className="addcoin-success-modal" onClick={(e) => e.stopPropagation()}>
              <div className="addcoin-success-icon">
                <i className="fas fa-check-circle" />
              </div>
              <h3 className="addcoin-success-title">Coin Added Successfully</h3>
              <p className="addcoin-success-desc">The following coin has been created:</p>
              <div className="addcoin-success-details">
                <div className="addcoin-success-row"><span className="addcoin-success-label">Name</span><span>{successModalData.name}</span></div>
                <div className="addcoin-success-row"><span className="addcoin-success-label">Symbol</span><span>{successModalData.shortName}</span></div>
                <div className="addcoin-success-row"><span className="addcoin-success-label">Category</span><span>{successModalData.category}</span></div>
                <div className="addcoin-success-row"><span className="addcoin-success-label">Chains</span><span>{successModalData.chains.join(", ")}</span></div>
              </div>
              <button type="button" className="btn addcoin-success-btn" onClick={handleSuccessModalClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AddSpotTradingCoinPage;
